import SendIcon from '@mui/icons-material/Send';
import { Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useState } from 'react';

import { useBaermannListByProtocolo } from '@/features/analisis/baermann/hooks/useBaermann';
import { useCoprocultivoListByProtocolo } from '@/features/analisis/coprocultivo/hooks/useCoprocultivo';
import { useEctoparasitosListByProtocolo } from '@/features/analisis/ectoparasitos/hooks/useEctoparasitos';
import { useHpgListByProtocolo } from '@/features/analisis/hpg/hooks/useHpg';
import { useOpgListByProtocolo } from '@/features/analisis/opg/hooks/useOpg';
import { useSedimentacionFhListByProtocolo } from '@/features/analisis/sedimentacion-fh/hooks/useSedimentacionFh';
import { EnviarInformeDialog } from '@/features/envios/components/EnviarInformeDialog';
import { EnvioRow } from '@/features/envios/components/EnvioRow';
import { useEnviosByProtocolo } from '@/features/envios/hooks/useEnvios';
import { useEstablecimiento } from '@/features/establecimientos/hooks/useEstablecimientos';
import { useProfesional } from '@/features/profesionales/hooks/useProfesionales';

import type { Protocolo } from '../types';

interface SeccionEnviosProps {
  uidProtocolo: string;
  protocolo: Protocolo;
  canManage: boolean;
}

export function SeccionEnvios({
  uidProtocolo,
  protocolo,
  canManage,
}: SeccionEnviosProps): JSX.Element | null {
  const [dialogOpen, setDialogOpen] = useState(false);

  const hpgQuery = useHpgListByProtocolo(uidProtocolo);
  const opgQuery = useOpgListByProtocolo(uidProtocolo);
  const coprocultivoQuery = useCoprocultivoListByProtocolo(uidProtocolo);
  const baermannQuery = useBaermannListByProtocolo(uidProtocolo);
  const sedFhQuery = useSedimentacionFhListByProtocolo(uidProtocolo);
  const ectoQuery = useEctoparasitosListByProtocolo(uidProtocolo);

  const profesionalQuery = useProfesional(protocolo.uid_profesional);
  const establecimientoQuery = useEstablecimiento(protocolo.uid_establecimiento);
  const enviosQuery = useEnviosByProtocolo(uidProtocolo);

  if (!canManage) return null;

  const totalAnalisis =
    (hpgQuery.data?.length ?? 0) +
    (opgQuery.data?.length ?? 0) +
    (coprocultivoQuery.data?.length ?? 0) +
    (baermannQuery.data?.length ?? 0) +
    (sedFhQuery.data?.length ?? 0) +
    (ectoQuery.data?.length ?? 0);
  const hayAnalisis = totalAnalisis > 0;

  const profesional = profesionalQuery.data;
  const establecimiento = establecimientoQuery.data;

  const envios = [...(enviosQuery.data ?? [])].sort(
    (a, b) =>
      new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime()
  );

  const canSend = hayAnalisis && !!profesional;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography variant="h6">Envíos</Typography>
          <Typography variant="body2" color="text.secondary">
            Enviá el informe al profesional por email.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<SendIcon />}
          disabled={!canSend}
          onClick={() => setDialogOpen(true)}
        >
          Enviar informe
        </Button>
      </Stack>

      {enviosQuery.isLoading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={60} />
          <Skeleton variant="rounded" height={60} />
        </Stack>
      ) : envios.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Todavía no se enviaron informes para este protocolo.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {envios.map((envio) => (
            <EnvioRow key={envio.uid_envio} envio={envio} />
          ))}
        </Stack>
      )}

      {profesional && (
        <EnviarInformeDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          uidProtocolo={uidProtocolo}
          finalYaEmitido={protocolo.informe_final_emitido}
          numeroProtocolo={protocolo.numero_protocolo}
          establecimientoNombre={establecimiento?.nombre ?? ''}
          profesional={profesional}
        />
      )}
    </Paper>
  );
}
